/**
 * Consumer hook: overlays in-flight optimistic connection operations onto query data.
 *
 * A component reads its own query data (`base`, pristine server truth) and overlays every
 * in-flight operation targeting the same owner/connection, gathered from TanStack Query's
 * mutation cache via `useMutationState` — no event bus, no broadcast; consumers of the same
 * connection independently select the same pending operations by `mutationKey`.
 *
 * Settle via `invalidateQueries` + refetch (or a live-update channel) rather than
 * `setQueryData(responseBody)`, so an out-of-order response can't fold a stale value in.
 */
import {useMutationState} from '@github-ui/react-query'
import {useMemo} from 'react'

import {
  type ConnectionOperation,
  type PendingConnectionOperation,
  applyOperations,
  connectionPathKey,
} from './connection-operations'

/** The shared root segment of every connection mutation key; `useMergedConnection`
 *  subscribes to this broad prefix (see the `useMutationState` call). */
export const CONNECTION_MUTATION_KEY_ROOT = 'connection'

/**
 * The stable, query-agnostic mutation key for a connection. Producers and consumers must
 * agree on this so pending operations can be gathered. The path stays a nested array (not
 * joined) so `['a','b']` and `['a.b']` produce distinct keys; TanStack hashes it structurally.
 */
export function connectionMutationKey(ownerId: string, connectionPath: string[]): unknown[] {
  return [CONNECTION_MUTATION_KEY_ROOT, ownerId, connectionPath]
}

/** The `useMutation` variables shape a connection mutation stores and this hook reads back. */
export type ConnectionMutationVariables = {operation: ConnectionOperation}

/** A pending operation read from the mutation cache, carrying the owner and path from its
 *  own key so a stale `useMutationState` snapshot can be filtered out synchronously. A
 *  superset of `PendingConnectionOperation`, so it feeds `applyOperations` directly. */
type PendingEntry = PendingConnectionOperation & {connectionPath: string[]}

/**
 * Overlays the still-pending operations for `{ownerId, connectionPath}` onto `base`. When
 * nothing is pending, returns `base` unchanged (stable identity → no needless re-render).
 */
export function useMergedConnection<T>({
  base,
  ownerId,
  connectionPath,
  enabled = true,
}: {
  base: T
  ownerId: string
  connectionPath: string[]
  /** When false, skip the overlay and return `base` unchanged. */
  enabled?: boolean
}): T {
  const pendingEntries = useMutationState<PendingEntry | undefined>({
    // Subscribe to the broad `['connection']` prefix, not this consumer's exact key:
    // `useMutationState` only recomputes on a mutation-cache notification, not when its
    // filter options change, so a key-scoped subscription would momentarily drop the newly
    // selected connection's already-pending operations when a mounted consumer switches
    // connections. The synchronous owner+path filter in the memo below narrows to this one.
    filters: {mutationKey: [CONNECTION_MUTATION_KEY_ROOT], status: 'pending'},
    // Carry the owner and path from the mutation's own key, in insertion (dispatch) order —
    // the last-write-wins tiebreaker `applyOperations` needs.
    select: mutation => {
      const operation = (mutation.state.variables as ConnectionMutationVariables | undefined)?.operation
      const key = mutation.options.mutationKey
      if (!operation || !Array.isArray(key) || key[0] !== CONNECTION_MUTATION_KEY_ROOT) return undefined
      return {ownerId: String(key[1]), connectionPath: Array.isArray(key[2]) ? (key[2] as string[]) : [], operation}
    },
  })

  // Compare connection paths by a stable, collision-free key; arrays are new each render.
  const pathKey = connectionPathKey(connectionPath)

  return useMemo(() => {
    if (!enabled) return base
    // Narrow the broad subscription to this connection by exact owner+path (so a
    // `['subIssues','nodes']` operation never leaks into a `['subIssues']` consumer),
    // staying correct the instant a mounted consumer switches owner/path.
    const operations = pendingEntries.filter(
      (entry): entry is PendingEntry =>
        entry != null && entry.ownerId === ownerId && connectionPathKey(entry.connectionPath) === pathKey,
    )
    return operations.length === 0 ? base : applyOperations(base, operations)
    // `pendingEntries` keeps a stable reference across renders (via `replaceEqualDeep`), so
    // it's a sound dependency directly — no hashing needed.
  }, [enabled, base, ownerId, pathKey, pendingEntries])
}
